import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Spinner } from "reactstrap";

import { AUTOPILOT_COLORS, autopilotInsetCardStyle } from "../../../components/autopilot/styles";
import { useToast } from "../../../hooks/toast";
import { salvarLote, testarConector, mensagemErroParametros } from "../../../services/parametrosApi";
import { situacaoCertificado } from "../../../services/embarcadorCertificadoApi";
import { renderControl } from "../Parametros/renderControl";
import { paramParaControle } from "../Parametros/porAssunto/paramParaControle";
import { calcularValoresParaSalvar, valoresAtuaisDoFormulario, valoresPorNome, chaveDe } from "../Parametros/porAssunto/calcularValoresParaSalvar";
import ConectorLogo from "./ConectorLogo";
import {
  drawerShellStyle,
  drawerHeadStyle,
  drawerEyebrowStyle,
  drawerTitleStyle,
  drawerSubtitleStyle,
  drawerCloseButtonStyle,
  drawerBodyStyle,
} from "../Transportadoras/detalheDrawer.styles";
import { camposDoConector, rotuloDoCampo, TEXTO_GERENCIADO, LINK_CERTIFICADO } from "./conectores";
import { STATUS } from "./statusDoConector";
import { StatusConectorBadge } from "./ConectorCard";
import "../Parametros/Parametros.css";

// Painel lateral do conector (H4, PO1-3262): descrição longa, avisos, campos por
// renderControl, "Testar conexão" (resultado inline), "Salvar"/"Ativar integração"
// (só o diff dos campos do conector) e "Desativar integração" com confirmação
// inline (grava '' no campoAtivador → o backend apaga a linha). SEFAZ não ativa nem
// desativa: a ativação é o certificado A1. V360/Oracle: só texto.
const secaoTituloStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  margin: "1rem 0 0.5rem",
};

const labelStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "0.84rem",
  fontWeight: 600,
  marginBottom: "0.3rem",
};

const ajudaStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.75rem",
  lineHeight: 1.5,
  marginTop: "0.3rem",
};

const avisoStyle = {
  border: `1px solid ${AUTOPILOT_COLORS.statusWarningBorder}`,
  background: AUTOPILOT_COLORS.statusWarningBg,
  color: AUTOPILOT_COLORS.text,
  borderRadius: 10,
  padding: "0.55rem 0.75rem",
  fontSize: "0.78rem",
  lineHeight: 1.5,
  marginBottom: "0.5rem",
};

function resultadoStyle(ok) {
  return {
    ...autopilotInsetCardStyle,
    border: `1px solid ${ok ? AUTOPILOT_COLORS.statusSuccessBorder : AUTOPILOT_COLORS.statusDangerBorder}`,
    background: ok ? AUTOPILOT_COLORS.statusSuccessBg : AUTOPILOT_COLORS.statusDangerBg,
    color: AUTOPILOT_COLORS.text,
    padding: "0.6rem 0.75rem",
    fontSize: "0.8rem",
    lineHeight: 1.5,
    marginTop: "0.75rem",
  };
}

function CertificadoResumo({ certificados }) {
  const lista = Array.isArray(certificados) ? certificados : [];
  const ativos = lista.filter((c) => c && c.ativo);
  if (!ativos.length) {
    return (
      <div style={{ ...autopilotInsetCardStyle, padding: "0.75rem 0.9rem", fontSize: "0.82rem", color: AUTOPILOT_COLORS.text }}>
        Nenhum certificado A1 ativo para este embarcador.{" "}
        <Link to={LINK_CERTIFICADO} style={{ color: AUTOPILOT_COLORS.accent, fontWeight: 600 }}>
          Enviar certificado
        </Link>
      </div>
    );
  }
  return (
    <div style={{ ...autopilotInsetCardStyle, padding: "0.75rem 0.9rem", fontSize: "0.82rem", color: AUTOPILOT_COLORS.text }}>
      {ativos.map((c) => {
        const s = situacaoCertificado(c);
        const cor = s.cor === "success" ? AUTOPILOT_COLORS.statusSuccess : s.cor === "warning" ? AUTOPILOT_COLORS.statusWarning : AUTOPILOT_COLORS.statusDanger;
        return (
          <div key={c.id} style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", padding: "0.2rem 0" }}>
            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.razaoSocial || c.cnpjCertificado || "Certificado A1"}</span>
            <strong style={{ color: cor, whiteSpace: "nowrap" }}>{s.rotulo}</strong>
          </div>
        );
      })}
      <div style={{ marginTop: "0.4rem" }}>
        <Link to={LINK_CERTIFICADO} style={{ color: AUTOPILOT_COLORS.accent, fontWeight: 600 }}>
          Gerenciar certificado digital
        </Link>
      </div>
    </div>
  );
}

export default function ConectorDrawer({ conector, status, formulario, certificados, embarcadorId, onClose, onSalvo }) {
  const { addToast } = useToast();
  const campos = useMemo(() => camposDoConector(conector, formulario), [conector, formulario]);
  const [alterados, setAlterados] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [resultadoTeste, setResultadoTeste] = useState(null);
  const [confirmandoDesativar, setConfirmandoDesativar] = useState(false);

  useEffect(() => {
    setAlterados({});
    setResultadoTeste(null);
    setConfirmandoDesativar(false);
  }, [conector, formulario]);

  const valoresAtuais = useMemo(() => valoresAtuaisDoFormulario(campos, alterados), [campos, alterados]);
  const diff = useMemo(() => calcularValoresParaSalvar(campos, alterados), [campos, alterados]);

  const notify = (type, title, description) => addToast({ place: "tr", type, title, description });

  // Chave = idParametro (String), como na tela de Parâmetros.
  const setValor = (chave, valor) => setAlterados((prev) => ({ ...prev, [chave]: valor }));

  const salvar = async () => {
    if (!diff.length) {
      notify("info", "Nada alterado", "Nenhum campo do conector foi alterado.");
      return;
    }
    setSalvando(true);
    try {
      await salvarLote("embarcador", embarcadorId, diff);
      notify("success", status === STATUS.ATIVA ? "Integração salva" : "Integração ativada", `${conector.nome}: ${diff.length} campo${diff.length === 1 ? "" : "s"} gravado${diff.length === 1 ? "" : "s"}.`);
      setAlterados({});
      if (onSalvo) onSalvo();
    } catch (e) {
      notify("danger", "Erro ao salvar", mensagemErroParametros(e, "Não foi possível salvar a integração."));
    } finally {
      setSalvando(false);
    }
  };

  const desativar = async () => {
    if (!conector.campoAtivador) return;
    setSalvando(true);
    try {
      const ativador = campos.find((p) => p.nome === conector.campoAtivador);
      const item = ativador ? { idParametro: ativador.idParametro, nome: ativador.nome, valor: "" } : { nome: conector.campoAtivador, valor: "" };
      await salvarLote("embarcador", embarcadorId, [item]);
      notify("success", "Integração desativada", `${conector.nome} não será mais consultado.`);
      setConfirmandoDesativar(false);
      setAlterados({});
      if (onSalvo) onSalvo();
    } catch (e) {
      notify("danger", "Erro ao desativar", mensagemErroParametros(e, "Não foi possível desativar a integração."));
    } finally {
      setSalvando(false);
    }
  };

  // Testa com o que está NA TELA (salvo + alterado), para validar antes de gravar.
  // O contrato do teste segue por NOME (campos de conector têm nome único).
  // Senha vazia não vai: o backend usa a salva.
  const testar = async () => {
    const valores = valoresPorNome(campos, valoresAtuais, { omitirSenhaVazia: true });
    setTestando(true);
    setResultadoTeste(null);
    try {
      const r = await testarConector(embarcadorId, conector.key, valores);
      setResultadoTeste({
        ok: !!(r && r.ok),
        mensagem: (r && r.mensagem) || (r && r.ok ? "Conexão validada." : "Não foi possível validar a conexão."),
        duracaoMs: r && typeof r.duracaoMs === "number" ? r.duracaoMs : null,
      });
    } catch (e) {
      setResultadoTeste({ ok: false, mensagem: mensagemErroParametros(e, "Falha ao testar a conexão."), duracaoMs: null });
    } finally {
      setTestando(false);
    }
  };

  const gerenciado = !!conector.gerenciado;
  const podeAtivarDesativar = !gerenciado && !conector.usaCertificado && !!conector.campoAtivador;
  const ocupado = salvando || testando;

  return (
    <div style={drawerShellStyle} className="conector-drawer">
      <div style={drawerHeadStyle}>
        <div style={{ minWidth: 0, display: "flex", alignItems: "flex-start", gap: "0.6rem" }}>
          <ConectorLogo conector={conector} height={40} />
          <div style={{ minWidth: 0 }}>
            <div style={drawerEyebrowStyle}>
              {conector.categoria} · {conector.direcao}
            </div>
            <div style={drawerTitleStyle}>{conector.nome}</div>
            <div style={{ ...drawerSubtitleStyle, display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.3rem" }}>
              <StatusConectorBadge status={status} />
            </div>
          </div>
        </div>
        <button type="button" onClick={onClose} style={drawerCloseButtonStyle} aria-label="Fechar detalhe">
          ×
        </button>
      </div>

      <div style={drawerBodyStyle}>
        <p style={{ color: AUTOPILOT_COLORS.text, fontSize: "0.84rem", lineHeight: 1.6, margin: 0 }}>{conector.descricaoLonga}</p>

        {(conector.avisos || []).length ? (
          <>
            <div style={secaoTituloStyle}>Atenção</div>
            {conector.avisos.map((a, i) => (
              <div key={i} style={avisoStyle}>
                {a}
              </div>
            ))}
          </>
        ) : null}

        {gerenciado ? (
          <div style={{ ...autopilotInsetCardStyle, marginTop: "1rem", padding: "0.9rem 1rem" }}>
            <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, fontSize: "0.88rem" }}>{TEXTO_GERENCIADO.titulo}</div>
            <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem", marginTop: "0.25rem" }}>{TEXTO_GERENCIADO.texto}</div>
          </div>
        ) : (
          <>
            {conector.usaCertificado ? (
              <>
                <div style={secaoTituloStyle}>Certificado digital</div>
                <CertificadoResumo certificados={certificados} />
              </>
            ) : null}

            <div style={secaoTituloStyle}>Configuração</div>
            {campos.length ? (
              campos.map((p) => {
                const { label, ajuda } = rotuloDoCampo(conector, p);
                const chave = chaveDe(p);
                const dirty = Object.prototype.hasOwnProperty.call(alterados, chave);
                return (
                  <div key={chave} style={{ marginBottom: "0.9rem", paddingLeft: "0.6rem", borderLeft: `3px solid ${dirty ? AUTOPILOT_COLORS.accent : "transparent"}` }}>
                    <div style={labelStyle}>{label}</div>
                    {renderControl({
                      param: paramParaControle(p),
                      valor: valoresAtuais[chave],
                      onChange: (v) => setValor(chave, v),
                      saving: ocupado,
                      fullWidth: true,
                    })}
                    {ajuda ? <div style={ajudaStyle}>{ajuda}</div> : null}
                  </div>
                );
              })
            ) : (
              <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.82rem" }}>
                Os parâmetros deste conector ainda não estão no catálogo do embarcador. Avise o suporte.
              </div>
            )}

            {resultadoTeste ? (
              <div style={resultadoStyle(resultadoTeste.ok)} role="status">
                <strong style={{ color: resultadoTeste.ok ? AUTOPILOT_COLORS.statusSuccess : AUTOPILOT_COLORS.statusDanger }}>
                  {resultadoTeste.ok ? "Conexão OK" : "Falha na conexão"}
                </strong>
                {resultadoTeste.duracaoMs !== null ? <span style={{ color: AUTOPILOT_COLORS.muted }}> · {resultadoTeste.duracaoMs} ms</span> : null}
                <div>{resultadoTeste.mensagem}</div>
              </div>
            ) : null}

            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "1.1rem" }}>
              <Button color="info" size="sm" outline onClick={testar} disabled={ocupado || !campos.length}>
                {testando ? <Spinner size="sm" /> : "Testar conexão"}
              </Button>
              <Button color="success" size="sm" onClick={salvar} disabled={ocupado || !diff.length}>
                {salvando && !confirmandoDesativar ? <Spinner size="sm" /> : status === STATUS.ATIVA || conector.usaCertificado ? "Salvar" : "Ativar integração"}
              </Button>
            </div>

            {podeAtivarDesativar && status === STATUS.ATIVA ? (
              <div style={{ marginTop: "1rem", paddingTop: "0.9rem", borderTop: `1px solid ${AUTOPILOT_COLORS.border}` }}>
                {confirmandoDesativar ? (
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem" }}>
                    <span style={{ color: AUTOPILOT_COLORS.statusDanger, fontSize: "0.78rem", flex: "1 1 100%" }}>
                      O Hub para de consultar {conector.nome} na hora e o campo de ativação é apagado. Desativar?
                    </span>
                    <Button color="danger" size="sm" disabled={salvando} onClick={desativar}>
                      {salvando ? "Desativando..." : "Confirmar desativação"}
                    </Button>
                    <Button color="secondary" size="sm" outline disabled={salvando} onClick={() => setConfirmandoDesativar(false)}>
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  <Button color="danger" size="sm" outline disabled={ocupado} onClick={() => setConfirmandoDesativar(true)}>
                    Desativar integração
                  </Button>
                )}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
